import React, { useEffect, useMemo } from "react";

import { Duration, Instant } from "@js-joda/core";
import {
  DeviceStatus,
  MdmObjectDeviceRelationDto,
  MdmObjectDto,
  useGetAllDevicesByProjectQuery,
  useGetAntennasByProjectQuery,
  useGetObjectQuery,
} from "gql/generated/types";
import L, { DivIcon, LatLngBounds, LatLngTuple } from "leaflet";
import icon from "leaflet/dist/images/marker-icon.png";
import iconShadow from "leaflet/dist/images/marker-shadow.png";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { LayersControl, Marker, Popup, useMap, ZoomControl, Circle, FeatureGroup } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-markercluster";
import { useDisplayTimezone } from "hooks/useTimezone";
import { Link } from "react-router-dom";
import { T } from "services/lang/LanguageService";
import { convertTimeSpanToString, fmtDatetime, secondsToTimeSpan, toInstant } from "utils/time-utils";
import useProjectContext from "../hooks/useProjectContext";
import { TableColumn } from "../utils/types";
import { renderDeviceStatus } from "../utils/utils";

export const defaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconAnchor: [12, 40],
  popupAnchor: [0, -40],
});

const offlineIcon = L.icon({
  iconUrl: icon,
  className: "offline-icon",
  shadowUrl: iconShadow,
  iconAnchor: [12, 40],
  popupAnchor: [0, -40],
});

const antennaIcon = L.divIcon({ className: "antenna-icon" });

type ObjectMapContentProps = {
  objects: MdmObjectDto[];
};

const ObjectMapContent: React.FC<ObjectMapContentProps> = ({ objects }) => {
  const { projectId, project } = useProjectContext();
  const map = useMap();

  const { data: deviceData } = useGetAllDevicesByProjectQuery({
    variables: { id: projectId! },
    skip: !projectId,
  });

  const offlineDevices = useMemo(
    () => deviceData?.mdmDevicesByProjectId?.filter(device => device.status.status === DeviceStatus.Offline),
    [deviceData?.mdmDevicesByProjectId]
  );

  const offlineObjects = useMemo(
    () =>
      objects?.filter(object =>
        offlineDevices?.some(device => device.objectRelation?.mdmObject?.id === object?.id)
      ),
    [objects, offlineDevices]
  );

  const antennaVisible = project?.mdmProjectById.configuration?.antennasConfiguration.visible;

  const markers = useMemo(
    () =>
      objects?.reduce((tuples, object) => {
        if (!object?.latitude || !object?.longitude) return tuples;
        tuples.push([object?.latitude, object?.longitude]);
        return tuples;
      }, [] as LatLngTuple[]),
    [objects]
  );

  useEffect(() => {
    if (markers.length === 0)
      map.fitBounds([
        [54.2, 23.5],
        [56.2, 24.5],
      ]);
    if (markers.length === 1) map.setView(markers[0], 16);
    if (markers.length > 1) {
      const bounds = new LatLngBounds(markers);
      const northEast = Object.values(bounds.getNorthEast()) as LatLngTuple;
      const southWest = Object.values(bounds.getSouthWest()) as LatLngTuple;
      map.fitBounds([northEast, southWest]);
    }
  }, [map, markers]);

  const markerGroups: LayerProps[] = [
    {
      layerName: T("mdm.all-objects"),
      markerIcon: defaultIcon,
      data: objects,
      checked: true,
    },
    {
      layerName: T("mdm.offline-objects"),
      markerIcon: offlineIcon,
      data: offlineObjects,
      checked: false,
    },
  ];

  return (
    <div>
      <ZoomControl position="bottomright" />
      <LayersControl position="topright">
        {markerGroups.map(({ layerName, markerIcon, data, checked }) => (
          <Layer
            key={layerName}
            layerName={layerName}
            markerIcon={markerIcon}
            data={data}
            checked={checked}
          />
        ))}
        {antennaVisible && <MapOverlays />}
      </LayersControl>
    </div>
  );
};

export default ObjectMapContent;

type LayerProps = {
  layerName: string;
  markerIcon: L.Icon<L.IconOptions> | DivIcon;
  data: MdmObjectDto[];
  checked: boolean;
};

const Layer: React.FC<LayerProps> = ({ layerName, markerIcon, data, checked }) => {
  const mapMarkers = data?.map(object => (
    <ObjectMarker key={`${object?.id}-${layerName}`} object={object} markerIcon={markerIcon} />
  ));

  return (
    <LayersControl.BaseLayer checked={checked} name={layerName}>
      <MarkerClusterGroup maxClusterRadius={28}>{mapMarkers}</MarkerClusterGroup>
    </LayersControl.BaseLayer>
  );
};

type ObjectMarkerProps = {
  markerIcon: L.Icon<L.IconOptions> | DivIcon;
  object: MdmObjectDto;
};

export const ObjectMarker: React.FC<ObjectMarkerProps> = ({ object, markerIcon }) => {
  if (!object?.latitude || !object?.longitude) return null;
  const position: LatLngTuple = [object.latitude, object.longitude];

  return (
    <Marker key={object.id} position={position} icon={markerIcon}>
      <Popup minWidth={320}>
        <ObjectPopup object={object} />
      </Popup>
    </Marker>
  );
};

const ObjectPopup = ({ object }: { object: MdmObjectDto }) => {
  const displayTz = useDisplayTimezone();
  const { projectId } = useProjectContext();

  const { data, loading } = useGetObjectQuery({ variables: { id: object.id! }, fetchPolicy: "no-cache" });

  const handleOffset = (rowData: MdmObjectDeviceRelationDto) => {
    const measurementTime = rowData.mdmDevice?.status.lastMeasurementDate;
    if (!measurementTime) return "-";
    const offset = Duration.between(toInstant(measurementTime) ?? Instant.now(), Instant.now());
    return convertTimeSpanToString(secondsToTimeSpan(offset.seconds()));
  };

  const handleDeviceLink = (rowData: MdmObjectDeviceRelationDto) => (
    <Link to={`/mdm/${projectId}/devices/${rowData.mdmDevice?.id}`} className="hover:underline">
      {rowData.mdmDevice?.identifier}
    </Link>
  );

  const columns: TableColumn<MdmObjectDeviceRelationDto>[] = [
    {
      field: "mdmDevice.identifier",
      header: T("word.device"),
      body: handleDeviceLink,
      columnKey: "mdmDevice.identifier",
    },
    {
      field: "mdmDevice.status.status",
      header: T("word.status"),
      body: rowData => renderDeviceStatus(rowData.mdmDevice),
      columnKey: "mdmDevice.status.status",
    },
    {
      field: "mdmDevice.status.lastMeasurementDate",
      header: T("mdm.last-measurement"),
      body: rowData =>
        rowData.mdmDevice?.status.lastMeasurementDate
          ? fmtDatetime(rowData.mdmDevice.status.lastMeasurementDate, displayTz)
          : "N/A",
      columnKey: "mdmDevice.status.lastMeasurementDate",
    },
    {
      header: "Δ",
      body: handleOffset,
      headerTooltip: T("mdm.time-since-last-measurement"),
      headerTooltipOptions: { position: "top" },
      columnKey: "mdmDevice.status.lastMeasurementDate",
    },
  ];
  return (
    <div className="flex flex-column align-items-start">
      <Link to={`/mdm/${projectId}/objects/${object.id}`} className="cursor-pointer hover:underline w-25rem">
        {object.title}
      </Link>
      <DataTable
        value={data?.mdmObjectById.deviceRelations}
        stripedRows
        loading={loading}
        size="small"
        className="mt-3 w-full"
      >
        {columns.map(col => (
          <Column key={col.header} {...col} />
        ))}
      </DataTable>
    </div>
  );
};

const MapOverlays: React.FC = () => {
  const { projectId } = useProjectContext();
  const { data } = useGetAntennasByProjectQuery({
    variables: { projectId: projectId! },
    skip: !projectId,
  });

  const antennas = data?.allAntennasByProjectId.map(antenna => (
    <>
      <Circle
        key={antenna.id}
        center={[antenna.latitude!, antenna.longitude!]}
        radius={antenna.radius!}
        stroke={false}
        fillColor={antenna.color!}
      />
      <Marker
        key={`${antenna.id}-marker`}
        position={[antenna.latitude!, antenna.longitude!]}
        icon={antennaIcon}
      >
        <Popup>
          {`${T("mdm.antenna")}:`}
          <Link to={`/mdm/${projectId}/antennas/${antenna.id}`} className="ml-3">
            {antenna.title}
          </Link>
        </Popup>
      </Marker>
    </>
  ));

  return (
    <LayersControl.Overlay name={T("mdm.coverage-area")}>
      <FeatureGroup>{antennas}</FeatureGroup>
    </LayersControl.Overlay>
  );
};
